import {
  AppModal,
  Badge,
  Button,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui";
import {
  PHASE_WIDGET_CATALOG,
  createPhaseInformationWidget,
} from "@/lib/data/static/phase-widget-catalog";
import type {
  PhaseActionDefinition,
  PhaseInformationWidget,
} from "@/lib/types";
import { Info, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { PhaseWidgetPreview } from "./PhaseWidgetPreview";

export function PhaseContentPickerModal({
  open,
  onOpenChange,
  actions,
  initialActionIds,
  initialWidgetTypes,
  canCreateAction,
  onCreateAction,
  onCreateActionGroup,
  onApply,
  stacked,
  showSampleData,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actions: PhaseActionDefinition[];
  initialActionIds: string[];
  initialWidgetTypes: PhaseInformationWidget["type"][];
  canCreateAction: boolean;
  onCreateAction: () => void;
  onCreateActionGroup: () => void;
  onApply: (
    actionIds: string[],
    widgetTypes: PhaseInformationWidget["type"][],
  ) => void;
  stacked: boolean;
  showSampleData: boolean;
}) {
  const [tab, setTab] = useState<"actions" | "widgets" | "custom" | "groups">(
    "actions",
  );
  const [search, setSearch] = useState("");
  const [selectedActionIds, setSelectedActionIds] = useState<string[]>([]);
  const [selectedWidgetTypes, setSelectedWidgetTypes] = useState<
    PhaseInformationWidget["type"][]
  >([]);

  useEffect(() => {
    if (!open) return;
    setTab("actions");
    setSearch("");
    setSelectedActionIds(initialActionIds);
    setSelectedWidgetTypes(initialWidgetTypes);
  }, [open, initialActionIds, initialWidgetTypes]);

  const query = search.trim().toLowerCase();
  const visibleActions = actions.filter(
    (action) =>
      (tab === "custom"
        ? action.kind === "Manual"
        : tab === "actions" && action.kind !== "Manual") &&
      `${action.title} ${action.description ?? ""}`
        .toLowerCase()
        .includes(query),
  );
  const visibleWidgets = PHASE_WIDGET_CATALOG.filter((widget) =>
    `${widget.title} ${widget.description}`.toLowerCase().includes(query),
  );
  const searchLabel =
    tab === "actions"
      ? "Search system actions"
      : tab === "custom"
        ? "Search custom actions"
        : tab === "widgets"
          ? "Search widgets"
          : "Search action groups";
  const selectionChanged =
    selectedActionIds.length !== initialActionIds.length ||
    selectedWidgetTypes.length !== initialWidgetTypes.length ||
    selectedActionIds.some((id) => !initialActionIds.includes(id)) ||
    selectedWidgetTypes.some((type) => !initialWidgetTypes.includes(type));

  const toggleAction = (id: string) => {
    setSelectedActionIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  };
  const toggleWidget = (type: PhaseInformationWidget["type"]) => {
    setSelectedWidgetTypes((current) =>
      current.includes(type)
        ? current.filter((item) => item !== type)
        : [...current, type],
    );
  };
  const applySelection = () => {
    onApply(selectedActionIds, selectedWidgetTypes);
    onOpenChange(false);
  };

  return (
    <AppModal
      open={open}
      onOpenChange={onOpenChange}
      title="Add action or widget"
      description="Select the actions and widgets to show in this phase."
      size="3xl"
      overlayClassName={stacked ? "z-[108]" : undefined}
      className={stacked ? "z-[109]" : undefined}
      bodyClassName="min-h-[min(60vh,560px)]"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!selectionChanged}
            onClick={applySelection}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs
            value={tab}
            onValueChange={(value) => {
              setTab(value as typeof tab);
            }}
          >
            <TabsList aria-label="Content type">
              <TabsTrigger value="actions">System actions</TabsTrigger>
              <TabsTrigger value="widgets">Widgets</TabsTrigger>
              <TabsTrigger value="custom">Custom actions</TabsTrigger>
              <TabsTrigger value="groups">Action groups</TabsTrigger>
            </TabsList>
          </Tabs>
          {tab === "custom" && canCreateAction ? (
            <Button type="button" variant="outline" onClick={onCreateAction}>
              <Plus className="mr-2 size-4" aria-hidden="true" />
              Create custom action
            </Button>
          ) : null}
        </div>
        {tab === "groups" ? (
          <div className="rounded-xl border border-dashed p-6">
            <p className="text-sm font-medium">Action groups</p>
            <p className="text-muted-foreground mt-1 text-sm">
              {initialActionIds.length === 0
                ? selectedActionIds.length > 0
                  ? "Update the phase with your selected actions, then reopen this picker to create a group."
                  : "Add an action to this phase before creating a group."
                : "Group related actions together in this phase."}
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-4"
              disabled={initialActionIds.length === 0}
              onClick={onCreateActionGroup}
            >
              <Plus className="mr-2 size-4" aria-hidden="true" />
              Create action group
            </Button>
          </div>
        ) : (
          <>
            <div className="relative max-w-md">
              <Search
                className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2"
                aria-hidden="true"
              />
              <Input
                aria-label={searchLabel}
                placeholder={searchLabel}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
                className="pl-9"
              />
            </div>
            {showSampleData ? (
              <p className="text-muted-foreground text-xs font-medium uppercase">
                Matter previews · sample data
              </p>
            ) : null}
            <div className="grid min-w-0 gap-4 md:grid-cols-2">
              {tab === "actions" || tab === "custom"
                ? visibleActions.map((action) => {
                    const selected = selectedActionIds.includes(action.id);
                    return (
                      <label
                        key={action.id}
                        className={`border-border/70 bg-card flex min-h-44 min-w-0 cursor-pointer flex-col justify-between gap-4 rounded-2xl border p-5 transition-colors ${selected ? "border-primary bg-primary/5 ring-primary ring-1" : "hover:bg-muted/30"}`}
                      >
                        <span className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              toggleAction(action.id);
                            }}
                            className="accent-primary mt-0.5 size-4 shrink-0 cursor-pointer"
                            aria-label={`Select ${action.title}`}
                          />
                          <span className="min-w-0 flex-1 text-sm font-medium break-words">
                            {action.title}
                          </span>
                          {action.description ? (
                            <span
                              title={action.description}
                              aria-label={action.description}
                            >
                              <Info
                                className="text-muted-foreground size-4 shrink-0"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </span>
                        {showSampleData ? (
                          <Badge
                            variant="outline"
                            className="text-muted-foreground w-fit font-normal"
                          >
                            Incomplete
                          </Badge>
                        ) : null}
                      </label>
                    );
                  })
                : visibleWidgets.map((widget) => {
                    const selected = selectedWidgetTypes.includes(widget.type);
                    return (
                      <label
                        key={widget.type}
                        className={`block min-w-0 cursor-pointer rounded-2xl transition-colors ${widget.wide ? "md:col-span-2" : ""} ${selected ? "ring-primary ring-2" : ""}`}
                      >
                        <PhaseWidgetPreview
                          widget={createPhaseInformationWidget(widget.type)}
                          actions={[]}
                          showSampleData={showSampleData}
                          headerTrailing={
                            <span className="flex shrink-0 items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => {
                                  toggleWidget(widget.type);
                                }}
                                className="accent-primary size-4 cursor-pointer"
                                aria-label={`Select ${widget.title}`}
                              />
                            </span>
                          }
                        />
                      </label>
                    );
                  })}
            </div>
            {(tab === "widgets" ? visibleWidgets : visibleActions).length ===
            0 ? (
              <p className="text-muted-foreground rounded-xl border border-dashed p-6 text-sm">
                {query
                  ? `No ${tab === "actions" ? "system actions" : tab === "custom" ? "custom actions" : "widgets"} match your search.`
                  : tab === "actions"
                    ? "No system actions available."
                    : tab === "custom"
                      ? "No custom actions yet. Create one to add it to this phase."
                      : "No widgets available."}
              </p>
            ) : null}
          </>
        )}
      </div>
    </AppModal>
  );
}
